import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { Badge, Button, Card, EmptyState, Skeleton } from '@/ui/primitives'
import { Camp } from '@/ui/form'
import { Bifa, BaraStare, CampParola, FaraDrept, Grup } from './comune'

type Setari = {
  qtravel_url?: string; qtravel_username?: string; qtravel_password_masked?: string
  sync_hour?: number; sync_minute?: number; sync_enabled?: boolean; days_back?: number
}
type Stare = {
  in_progress: boolean; last_sync: string | null; last_sync_status: string | null
  last_sync_message: string | null; next_sync: string | null
}
type Rand = {
  id: number; type: string; filename: string | null; records: number | null
  records_new: number | null; records_updated: number | null; status: string
  error: string | null; duration_seconds: number | null; created_at?: string | null
}

const clipa = (s: string | null | undefined) =>
  s ? new Date(s).toLocaleString('ro-RO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

export function SetariSincronizare() {
  const qc = useQueryClient()
  const [f, setF] = useState<Setari & { parola: string }>({ parola: '' })
  const [salvat, setSalvat] = useState(false)
  const [eroare, setEroare] = useState<string | null>(null)
  const [zile, setZile] = useState('3')

  const setari = useQuery({
    queryKey: ['sync-setari'],
    queryFn: () => api.get<{ settings: Setari }>('/sync/settings'),
    retry: false,
  })
  /** Cand sincronizarea e in curs, starea se reciteste singura — altfel omul ramane cu
   *  „in curs" pe ecran si nu stie daca s-a terminat. */
  const stare = useQuery({
    queryKey: ['sync-stare'],
    queryFn: () => api.get<Stare>('/sync/status'),
    retry: false,
    refetchInterval: (q) => (q.state.data?.in_progress ? 5000 : false),
  })
  const istoric = useQuery({
    queryKey: ['sync-istoric'],
    queryFn: () => api.get<{ history: Rand[] }>('/sync/history', { limit: 20 }),
    retry: false,
  })
  const loguri = useQuery({
    queryKey: ['sync-loguri'],
    queryFn: () => api.get<{ logs: string }>('/sync/logs', { lines: 150 }),
    retry: false,
  })

  useEffect(() => { if (setari.data?.settings) setF((x) => ({ ...setari.data.settings, parola: x.parola })) }, [setari.data])

  const faraDrept = [setari.error, stare.error].some((e) => (e as ApiError | null)?.status === 403)

  const salveaza = useMutation({
    mutationFn: () => api.post('/sync/settings', {
      qtravel_url: f.qtravel_url ?? '',
      qtravel_username: f.qtravel_username ?? '',
      qtravel_password: f.parola,
      sync_hour: Number(f.sync_hour ?? 0),
      sync_minute: Number(f.sync_minute ?? 15),
      sync_enabled: Boolean(f.sync_enabled),
      days_back: Number(f.days_back ?? 3),
    }),
    onSuccess: () => {
      setSalvat(true); setF((x) => ({ ...x, parola: '' }))
      qc.invalidateQueries({ queryKey: ['sync-setari'] })
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  const declanseaza = useMutation({
    mutationFn: (zileInUrma: number) => api.post(`/sync/trigger?days_back=${zileInUrma}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sync-stare'] })
      qc.invalidateQueries({ queryKey: ['sync-istoric'] })
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu am putut porni sincronizarea'),
  })

  if (faraDrept) return <div className="px-8 py-6"><FaraDrept ce="Sincronizarea cu QTravel" /></div>
  if (setari.isPending) return <div className="px-8 py-6"><Card><Skeleton rows={8} /></Card></div>

  const s = stare.data

  return (
    <div className="px-8 py-6 space-y-5">
      <div className="grid gap-3 sm:grid-cols-4">
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Acum</p>
          <p className="mt-1 text-[15px]">{s?.in_progress ? 'se sincronizeaza…' : 'in asteptare'}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Ultima sincronizare</p>
          <p className="mt-1 text-[15px]">{clipa(s?.last_sync)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Rezultat</p>
          <p className="mt-1">
            <Badge tone={s?.last_sync_status === 'success' ? 'good' : s?.last_sync_status ? 'bad' : 'neutral'}>
              {s?.last_sync_status ?? 'necunoscut'}
            </Badge>
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Urmatoarea</p>
          <p className="mt-1 text-[15px] tnum">{s?.next_sync ?? '—'}</p>
        </Card>
      </div>
      {s?.last_sync_message && (
        <Card className="p-3"><p className="text-[13px] text-muted">{s.last_sync_message}</p></Card>
      )}

      <Grup titlu="Legatura cu QTravel Backoffice">
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Camp eticheta="Adresa" id="q-url" valoare={f.qtravel_url ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, qtravel_url: v })) }} latime="plin" />
          <Camp eticheta="Utilizator" id="q-user" valoare={f.qtravel_username ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, qtravel_username: v })) }} />
          <CampParola eticheta="Parola" id="q-pass" valoare={f.parola} masca={f.qtravel_password_masked}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, parola: v })) }} />
        </div>
      </Grup>

      <Grup titlu="Sincronizare automata">
        <div className="grid gap-4 sm:grid-cols-3 max-w-2xl">
          <Camp eticheta="Ora" id="q-ora" tip="number" valoare={String(f.sync_hour ?? 0)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sync_hour: Number(v) })) }} />
          <Camp eticheta="Minut" id="q-min" tip="number" valoare={String(f.sync_minute ?? 15)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sync_minute: Number(v) })) }} />
          <Camp eticheta="Zile in urma" id="q-zile" tip="number" valoare={String(f.days_back ?? 3)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, days_back: Number(v) })) }}
            indiciu="Cat de mult inapoi se recitesc rezervarile" />
        </div>
        <div className="mt-3">
          <Bifa eticheta="Porneste sincronizarea automata" valoare={Boolean(f.sync_enabled)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sync_enabled: v })) }} />
        </div>
        <div className="mt-4">
          <BaraStare salveaza={() => { setEroare(null); salveaza.mutate() }} seSalveaza={salveaza.isPending}
            salvat={salvat} eroare={eroare} mesajSalvat="Salvat" />
        </div>
      </Grup>

      <Grup titlu="Sincronizare acum">
        <div className="flex flex-wrap items-end gap-3 max-w-2xl">
          <div className="w-32">
            <Camp eticheta="Zile in urma" id="q-manual" tip="number" valoare={zile} seteaza={setZile} />
          </div>
          <Button variant="primary" onClick={() => declanseaza.mutate(Number(zile) || 0)}
            disabled={declanseaza.isPending || s?.in_progress}>
            {s?.in_progress ? 'Se sincronizeaza…' : 'Porneste acum'}
          </Button>
          <span className="text-[12.5px] text-muted">
            Aduce rezervarile si facturile din ultimele {zile || 0} zile.
          </span>
        </div>
      </Grup>

      <Grup titlu="Ultimele sincronizari">
        {istoric.data?.history?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[13.5px]">
              <thead className="text-[12.5px] text-muted border-b border-line">
                <tr>
                  <th className="text-left font-medium py-2">Tip</th>
                  <th className="text-left font-medium py-2">Fisier</th>
                  <th className="text-right font-medium py-2">Inregistrari</th>
                  <th className="text-right font-medium py-2">Noi</th>
                  <th className="text-right font-medium py-2">Actualizate</th>
                  <th className="text-right font-medium py-2">Durata</th>
                  <th className="text-left font-medium py-2">Stare</th>
                </tr>
              </thead>
              <tbody>
                {istoric.data.history.map((h) => (
                  <tr key={h.id} className="border-b border-line last:border-0">
                    <td className="py-2">{h.type}</td>
                    <td className="py-2 text-muted">{h.filename ?? '—'}</td>
                    <td className="py-2 text-right tnum">{h.records ?? 0}</td>
                    <td className="py-2 text-right tnum">{h.records_new ?? 0}</td>
                    <td className="py-2 text-right tnum">{h.records_updated ?? 0}</td>
                    <td className="py-2 text-right tnum">{h.duration_seconds ? `${h.duration_seconds}s` : '—'}</td>
                    <td className="py-2">
                      <Badge tone={h.status === 'success' ? 'good' : 'bad'}>{h.status}</Badge>
                      {h.error && <span className="ml-2 text-[12px] text-bad">{h.error}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState title="Nicio sincronizare inregistrata" />
        )}
      </Grup>

      <Grup titlu="Jurnalul sincronizarii">
        <pre className="text-[12px] leading-[1.5] bg-page rounded-lg p-3 max-h-80 overflow-auto whitespace-pre-wrap">
          {loguri.data?.logs || 'Fara jurnal.'}
        </pre>
      </Grup>
    </div>
  )
}
